/**
 * usePlanStatus — single source of truth for what plan the user is on,
 * how many credits remain, when the plan expires, and whether we should
 * auto-prompt an upgrade.
 *
 * Data sources:
 *   user_credits.credits_balance        → current credits
 *   user_subscriptions (active row)     → plan name, period end, credits allocated
 *
 * Plan ID mapping (DB → display):
 *   null / no active sub  → 'free'
 *   'pack-monthly'        → 'Monthly'
 *   'pack-quarterly'      → 'Quarterly'
 *   'pack-halfyearly'     → 'Half-Yearly'
 *   'professional' etc.   → legacy names kept as-is
 *
 * Use PlanStatusProvider around the dashboard so Layout, AICompanion, sidebar
 * and all consumers share the same state; one refresh() updates everyone.
 */

import React, { useState, useEffect, useCallback, useMemo, createContext, useContext } from 'react';

import { supabase } from '../lib/supabase';
import {
  CREDITS_UPDATED_EVENT,
  type CreditsUpdatedDetail,
} from '../utils/creditsSync';

export type PlanKey = 'free' | 'monthly' | 'quarterly' | 'halfyearly' | 'legacy';

export interface PlanStatus {
  /** Raw plan_id from subscription or last completed purchase */
  planId: string | null;
  /** Short key used for styling / feature gating */
  planKey: PlanKey;
  /** Display name shown in UI (e.g. "Day Pass", "Weekly", "FREE") */
  planDisplayName: string;
  /** Current credits balance */
  credits: number;
  /** Credits allocated in current period (0 for free) */
  creditsAllocated: number;
  /** ISO string of when the paid plan ends; null for free */
  periodEnd: string | null;
  /** Days remaining in current period (null = free / unknown) */
  daysLeft: number | null;
  /** True when a paid subscription exists and is within its period */
  isActive: boolean;
  /** True when plan exists but current_period_end < now */
  isExpired: boolean;
  /** True when credits === 0 */
  isOutOfCredits: boolean;
  /** True when credits ≤ lowThreshold (default 3) */
  isLowCredits: boolean;
  /** Admin bypass or sentinel balance — show "Unlimited" in the top bar. */
  isUnlimitedCredits: boolean;
  /**
   * True when we should prompt the upgrade modal.
   * Conditions: credits = 0 OR paid plan expired OR ≤ 1 day left on paid plan.
   */
  shouldPromptUpgrade: boolean;
  loading: boolean;
  refresh: () => void;
}

const LOW_CREDITS_THRESHOLD = 3;

const PlanStatusContext = createContext<PlanStatus | null>(null);

function applyCreditsDetail(
  detail: CreditsUpdatedDetail | undefined,
  setCredits: React.Dispatch<React.SetStateAction<number>>,
  refresh: () => void,
): void {
  if (typeof detail?.newBalance === 'number' && Number.isFinite(detail.newBalance)) {
    setCredits(detail.newBalance);
    return;
  }
  if (typeof detail?.delta === 'number' && Number.isFinite(detail.delta)) {
    setCredits((c) => Math.max(0, Math.round((c + detail.delta!) * 100) / 100));
    return;
  }
  refresh();
}

/** Instant pill updates + realtime server debits. */
function useCreditsLiveSync(
  setCredits: React.Dispatch<React.SetStateAction<number>>,
  refresh: () => void,
): void {
  useEffect(() => {
    const onCreditsUpdated = (e: Event) => {
      applyCreditsDetail((e as CustomEvent<CreditsUpdatedDetail>).detail, setCredits, refresh);
    };
    window.addEventListener(CREDITS_UPDATED_EVENT, onCreditsUpdated);
    return () => window.removeEventListener(CREDITS_UPDATED_EVENT, onCreditsUpdated);
  }, [refresh, setCredits]);

  useEffect(() => {
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    void (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      channel = supabase
        .channel(`user-credits-${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'user_credits',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            const raw = (payload.new as { credits_balance?: number | string })?.credits_balance;
            const balance = Number(raw);
            if (!Number.isFinite(balance)) return;
            setCredits(balance);
            window.dispatchEvent(
              new CustomEvent<CreditsUpdatedDetail>(CREDITS_UPDATED_EVENT, {
                detail: { newBalance: balance },
              }),
            );
          },
        )
        .subscribe();
    })();

    return () => {
      cancelled = true;
      if (channel) void supabase.removeChannel(channel);
    };
  }, [refresh, setCredits]);
}

/** Map DB plan_id → { key, display } */
function mapPlanId(planId: string | null | undefined): { key: PlanKey; display: string } {
  if (!planId) return { key: 'free', display: 'FREE' };
  switch (planId) {
    case 'pack-monthly':    return { key: 'monthly', display: 'Monthly' };
    case 'pack-quarterly':  return { key: 'quarterly', display: 'Quarterly' };
    case 'pack-halfyearly': return { key: 'halfyearly', display: 'Half-Yearly' };
    case 'pack-daypass':
    case 'pack-weekly':     return { key: 'legacy', display: 'Legacy' };
    // Legacy plan IDs kept for backwards compatibility
    case 'professional':  return { key: 'legacy',  display: 'Pro'      };
    case 'premium':       return { key: 'legacy',  display: 'Premium'  };
    case 'enterprise':    return { key: 'legacy',  display: 'Enterprise' };
    default:              return { key: 'legacy',  display: planId     };
  }
}

function derivePlanStatus(
  credits: number,
  planId: string | null,
  creditsAllocated: number,
  periodEnd: string | null,
  loading: boolean,
  refresh: () => void,
  creditBypassEnabled = false,
): PlanStatus {
  const { key: planKey, display: planDisplayName } = mapPlanId(planId);
  const now = new Date();
  const endDate = periodEnd ? new Date(periodEnd) : null;
  const isExpired = endDate ? endDate < now : false;
  const daysLeft = endDate
    ? Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / 86_400_000))
    : null;
  const isActive = !!planId && !isExpired;
  const isUnlimitedCredits = creditBypassEnabled;
  const isOutOfCredits = !isUnlimitedCredits && credits === 0;
  const isLowCredits = !isUnlimitedCredits && credits > 0 && credits <= LOW_CREDITS_THRESHOLD;
  const shouldPromptUpgrade =
    !isUnlimitedCredits && (isOutOfCredits || isExpired || (daysLeft !== null && daysLeft <= 1));
  return {
    planId,
    planKey,
    planDisplayName,
    credits,
    creditsAllocated,
    periodEnd,
    daysLeft,
    isActive,
    isExpired,
    isOutOfCredits,
    isLowCredits,
    isUnlimitedCredits,
    shouldPromptUpgrade,
    loading,
    refresh,
  };
}

/** Duration in hours each pack plan is considered "active" after purchase. */
const PACK_DURATION_HOURS: Record<string, number> = {
  'pack-monthly': 30 * 24,
  'pack-quarterly': 90 * 24,
  'pack-halfyearly': 180 * 24,
};

/**
 * When no active user_subscriptions row exists (credit pack purchases may not
 * create one), fall back to the most-recent completed credit_transaction to
 * infer the current plan and its expiry.
 */
async function inferPlanFromTransactions(userId: string): Promise<{
  planId: string | null;
  creditsAllocated: number;
  periodEnd: string | null;
}> {
  try {
    const { data: txRow } = await supabase
      .from('credit_transactions')
      .select('metadata, created_at, credits_amount')
      .eq('user_id', userId)
      .eq('status', 'completed')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!txRow?.metadata) return { planId: null, creditsAllocated: 0, periodEnd: null };

    const meta = txRow.metadata as Record<string, unknown>;
    const planId = (meta.planId ?? meta.plan_id ?? null) as string | null;
    if (!planId) return { planId: null, creditsAllocated: 0, periodEnd: null };

    const hours = PACK_DURATION_HOURS[planId];
    if (!hours) return { planId: null, creditsAllocated: 0, periodEnd: null };

    const createdAt = new Date(txRow.created_at as string);
    const periodEndDate = new Date(createdAt.getTime() + hours * 3600 * 1000);
    const now = new Date();

    if (periodEndDate <= now) return { planId: null, creditsAllocated: 0, periodEnd: null };

    return {
      planId,
      creditsAllocated: typeof txRow.credits_amount === 'number' ? txRow.credits_amount : 0,
      periodEnd: periodEndDate.toISOString(),
    };
  } catch {
    return { planId: null, creditsAllocated: 0, periodEnd: null };
  }
}

/** Shared plan/credits state for dashboard. Wrap dashboard with this so Layout, AICompanion, sidebar all see the same data. */
export function PlanStatusProvider({ children }: { children: React.ReactNode }) {
  const [credits, setCredits] = useState(0);
  const [planId, setPlanId] = useState<string | null>(null);
  const [creditsAllocated, setAlloc] = useState(0);
  const [periodEnd, setPeriodEnd] = useState<string | null>(null);
  const [creditBypassEnabled, setCreditBypassEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  // Track whether we've completed at least one successful load so subsequent refreshes
  // don't flash a loading state and cause the credits counter to blink.
  const hasLoadedRef = React.useRef(false);

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useCreditsLiveSync(setCredits, refresh);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [refresh]);

  // Listen for post-payment refresh signals from DashboardContainer
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const onPaymentRefresh = (e: Event) => {
      const detail = (e as CustomEvent<CreditsUpdatedDetail>).detail;
      if (detail?.newBalance != null || detail?.delta != null) {
        applyCreditsDetail(detail, setCredits, refresh);
      }
      timers.push(setTimeout(refresh, 800));
      timers.push(setTimeout(refresh, 3000));
      timers.push(setTimeout(refresh, 7000));
    };
    window.addEventListener('planStatusRefresh', onPaymentRefresh);
    return () => {
      window.removeEventListener('planStatusRefresh', onPaymentRefresh);
      timers.forEach(clearTimeout);
    };
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      // Only show loading spinner on the very first fetch; subsequent refreshes
      // silently update in the background to avoid UI flicker.
      if (!hasLoadedRef.current) setLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || cancelled) {
          setLoading(false);
          return;
        }
        const [{ data: creditRow, error: creditError }, { data: userRow }, { data: subRow }, { data: packSubRow }] = await Promise.all([
          supabase
            .from('user_credits')
            .select('credits_balance')
            .eq('user_id', user.id)
            .maybeSingle(),
          supabase
            .from('users')
            .select('credit_bypass_enabled')
            .eq('user_id', user.id)
            .maybeSingle(),
          supabase
            .from('user_subscriptions')
            .select('plan_id, current_period_end, credits_allocated, status')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase
            .from('pack_subscriptions')
            .select('pack_id, current_period_end, credits_per_period, status, cancel_at_period_end')
            .eq('user_id', user.id)
            .in('status', ['active', 'bank_approval_pending'])
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
        ]);

        let resolvedPlanId = packSubRow?.pack_id ?? subRow?.plan_id ?? null;
        let resolvedAlloc = packSubRow?.credits_per_period ?? subRow?.credits_allocated ?? 0;
        let resolvedPeriodEnd = packSubRow?.current_period_end ?? subRow?.current_period_end ?? null;

        if (!packSubRow && !subRow) {
          const inferred = await inferPlanFromTransactions(user.id);
          if (inferred.planId) {
            resolvedPlanId = inferred.planId;
            resolvedAlloc = inferred.creditsAllocated;
            resolvedPeriodEnd = inferred.periodEnd;
          }
        }

        if (!cancelled) {
          const defaultFreeCredits = 5;
          const hasValidRow =
            !creditError &&
            creditRow != null &&
            typeof creditRow === 'object' &&
            Number.isFinite(Number((creditRow as { credits_balance?: number }).credits_balance));
          const rawCredits = hasValidRow
            ? Number((creditRow as { credits_balance: number }).credits_balance)
            : undefined;
          const isFree = resolvedPlanId == null;
          const creditsToSet =
            hasValidRow
              ? (rawCredits ?? 0)
              : isFree
                ? defaultFreeCredits
                // Paid user with no/invalid credits row — fall back to credits allocated
                // so their access isn't incorrectly zeroed out due to a DB miss.
                : resolvedAlloc > 0 ? resolvedAlloc : 1;
          setCredits(creditsToSet);
          setPlanId(resolvedPlanId);
          setAlloc(resolvedAlloc);
          setPeriodEnd(resolvedPeriodEnd);
          setCreditBypassEnabled(userRow?.credit_bypass_enabled === true);
          hasLoadedRef.current = true;
        }
      } catch (e) {
        console.error('[usePlanStatus]', e);
        if (!cancelled) setCredits(3);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const value = useMemo(
    () => derivePlanStatus(credits, planId, creditsAllocated, periodEnd, loading, refresh, creditBypassEnabled),
    [credits, planId, creditsAllocated, periodEnd, loading, refresh, creditBypassEnabled]
  );
  return React.createElement(PlanStatusContext.Provider, { value }, children);
}

export function usePlanStatus(): PlanStatus {
  const ctx = useContext(PlanStatusContext);
  if (ctx != null) return ctx;

  // Fallback when outside PlanStatusProvider (e.g. non-dashboard pages)
  const [credits, setCredits] = useState(0);
  const [planId, setPlanId] = useState<string | null>(null);
  const [creditsAllocated, setAlloc] = useState(0);
  const [periodEnd, setPeriodEnd] = useState<string | null>(null);
  const [creditBypassEnabled, setCreditBypassEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useCreditsLiveSync(setCredits, refresh);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [refresh]);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const onPaymentRefresh = (e: Event) => {
      const detail = (e as CustomEvent<CreditsUpdatedDetail>).detail;
      if (detail?.newBalance != null || detail?.delta != null) {
        applyCreditsDetail(detail, setCredits, refresh);
      }
      timers.push(setTimeout(refresh, 800));
      timers.push(setTimeout(refresh, 3000));
    };
    window.addEventListener('planStatusRefresh', onPaymentRefresh);
    return () => {
      window.removeEventListener('planStatusRefresh', onPaymentRefresh);
      timers.forEach(clearTimeout);
    };
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || cancelled) {
          setLoading(false);
          return;
        }
        const [{ data: creditRow, error: creditError }, { data: userRow }, { data: subRow }, { data: packSubRow }] = await Promise.all([
          supabase
            .from('user_credits')
            .select('credits_balance')
            .eq('user_id', user.id)
            .maybeSingle(),
          supabase
            .from('users')
            .select('credit_bypass_enabled')
            .eq('user_id', user.id)
            .maybeSingle(),
          supabase
            .from('user_subscriptions')
            .select('plan_id, current_period_end, credits_allocated, status')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase
            .from('pack_subscriptions')
            .select('pack_id, current_period_end, credits_per_period, status, cancel_at_period_end')
            .eq('user_id', user.id)
            .in('status', ['active', 'bank_approval_pending'])
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
        ]);

        let resolvedPlanId = packSubRow?.pack_id ?? subRow?.plan_id ?? null;
        let resolvedAlloc = packSubRow?.credits_per_period ?? subRow?.credits_allocated ?? 0;
        let resolvedPeriodEnd = packSubRow?.current_period_end ?? subRow?.current_period_end ?? null;

        if (!packSubRow && !subRow) {
          const inferred = await inferPlanFromTransactions(user.id);
          if (inferred.planId) {
            resolvedPlanId = inferred.planId;
            resolvedAlloc = inferred.creditsAllocated;
            resolvedPeriodEnd = inferred.periodEnd;
          }
        }

        if (!cancelled) {
          const defaultFreeCredits = 5;
          const hasValidRow =
            !creditError &&
            creditRow != null &&
            typeof creditRow === 'object' &&
            Number.isFinite(Number((creditRow as { credits_balance?: number }).credits_balance));
          const rawCredits = hasValidRow
            ? Number((creditRow as { credits_balance: number }).credits_balance)
            : undefined;
          const isFree = resolvedPlanId == null;
          const creditsToSet = hasValidRow
            ? (rawCredits ?? 0)
            : isFree
              ? defaultFreeCredits
              // Paid user with no/invalid credits row — fall back to credits allocated
              : resolvedAlloc > 0 ? resolvedAlloc : 1;
          setCredits(creditsToSet);
          setPlanId(resolvedPlanId);
          setAlloc(resolvedAlloc);
          setPeriodEnd(resolvedPeriodEnd);
          setCreditBypassEnabled(userRow?.credit_bypass_enabled === true);
        }
      } catch (e) {
        console.error('[usePlanStatus]', e);
        if (!cancelled) setCredits(3);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return derivePlanStatus(credits, planId, creditsAllocated, periodEnd, loading, refresh, creditBypassEnabled);
}

/** Feature map — what each plan tier can access */
export const PLAN_FEATURES = {
  free: {
    optimizations: 3,          // lifetime
    aiModel: 'Groq (fast)',
    download: false,
    fullHistory: false,
    allModels: false,
    priority: false,
    calendarReminders: false,
  },
  monthly: {
    optimizations: 60,
    aiModel: 'All models',
    download: true,
    fullHistory: true,
    allModels: true,
    priority: true,
    calendarReminders: true,
  },
  quarterly: {
    optimizations: 180,
    aiModel: 'All models',
    download: true,
    fullHistory: true,
    allModels: true,
    priority: true,
    calendarReminders: true,
  },
  halfyearly: {
    optimizations: 360,
    aiModel: 'All models',
    download: true,
    fullHistory: true,
    allModels: true,
    priority: true,
    calendarReminders: true,
  },
  legacy: {
    optimizations: 50,
    aiModel: 'GPT-4o',
    download: true,
    fullHistory: true,
    allModels: true,
    priority: false,
    calendarReminders: false,
  },
} satisfies Record<PlanKey, object>;
